import { useState, useEffect } from 'preact/hooks';
import { MainLayout } from '../../components/MainLayout';
import { getAuthHeaders } from '../../utils/auth';
import { scheduledWorkflowsService } from '../../services/scheduled-workflows.service';
import type { SupportedModel } from '../../services/scheduled-workflows.service';
import type { TeamAiKeyRef, TeamAiProvider as AiProvider } from '@zenmako/contracts/teams';

const PROVIDER_LABELS: Record<AiProvider, string> = {
  openai: 'OpenAI',
  google: 'Google AI',
};

interface Team {
  teamId: string;
  name: string;
  description?: string;
  memberCount: number;
  role: 'admin' | 'member';
  status: 'active' | 'deactivated';
}

interface TeamSettingsPageProps {
  teamId: string;
}

export function TeamSettingsPage({ teamId }: TeamSettingsPageProps) {
  const [team, setTeam] = useState<Team | null>(null);
  const [userRole, setUserRole] = useState<string>('');
  const [activeTab, setActiveTab] = useState<string>('general');
  const [teamName, setTeamName] = useState('');
  const [description, setDescription] = useState('');
  const [members, setMembers] = useState<any[]>([]);
  const [invitations, setInvitations] = useState<any[]>([]);
  const [billingInfo, setBillingInfo] = useState<any>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'member'>('member');
  const [inviting, setInviting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [upgradingPlan, setUpgradingPlan] = useState<boolean>(false);
  const [managingSubscription, setManagingSubscription] = useState(false);
  const [supportedModels, setSupportedModels] = useState<SupportedModel[]>([]);
  const [aiKeys, setAiKeys] = useState<TeamAiKeyRef[]>([]);
  const [aiKeyInput, setAiKeyInput] = useState('');
  const [aiProviderInput, setAiProviderInput] = useState<AiProvider>('openai');
  const [aiLabelInput, setAiLabelInput] = useState('');
  const [showAddAiKey, setShowAddAiKey] = useState(false);
  const [savingAiKey, setSavingAiKey] = useState(false);
  const [deletingAiKeyId, setDeletingAiKeyId] = useState<string | null>(null);
  const [updatingAiKeyId, setUpdatingAiKeyId] = useState<string | null>(null);
  const [defaultPrimaryModel, setDefaultPrimaryModel] = useState('');
  const [defaultSecondaryModel, setDefaultSecondaryModel] = useState('');
  const [savingModels, setSavingModels] = useState(false);

  useEffect(() => {
    loadTeamDetails();
    loadTeamMembers();
    loadTeamInvitations();
    loadBillingInfo();
    loadSupportedModels();
    loadAiKeyInfo();
  }, [teamId]);

  const loadTeamDetails = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/teams/${teamId}`, {
        headers: {
          ...(await getAuthHeaders())
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load team details');
      }

      const data = await response.json();
      setTeam(data.team);
      setUserRole(data.userRole);
      setTeamName(data.team.name);
      setDescription(data.team.description || '');
      setDefaultPrimaryModel(data.team.defaultPrimaryModel || '');
      setDefaultSecondaryModel(data.team.defaultSecondaryModel || '');
    } catch (err) {
      console.error('Error loading team details:', err);
      setError(err instanceof Error ? err.message : 'Failed to load team');
    } finally {
      setLoading(false);
    }
  };

  const loadTeamMembers = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/teams/${teamId}/members`, {
        headers: {
          ...(await getAuthHeaders())
        }
      });

      if (response.ok) {
        const data = await response.json();
        const flattenedMembers = (data.members || []).map((member: any) => ({
          ...member,
          name: member.user?.name || member.user?.email || 'Unknown User',
          email: member.user?.email || 'No email'
        }));
        setMembers(flattenedMembers);
      }
    } catch (err) {
      console.error('Error loading team members:', err);
    }
  };

  const loadTeamInvitations = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/teams/${teamId}/invitations`, {
        headers: {
          ...(await getAuthHeaders())
        }
      });

      if (response.ok) {
        const data = await response.json();
        setInvitations(data.invitations || []);
      }
    } catch (err) {
      console.error('Error loading team invitations:', err);
    }
  };

  const loadBillingInfo = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/teams/${teamId}/billing`, {
        headers: {
          ...(await getAuthHeaders())
        }
      });

      if (response.ok) {
        const data = await response.json();
        setBillingInfo(data);
      } else {
        // TODO: Billing endpoint not implemented yet - should get limits from API
        // Don't set fake data that violates single source of truth
        setBillingInfo(null);
      }
    } catch (err) {
      console.error('Error loading billing info:', err);
      setBillingInfo(null);
    }
  };

  const loadSupportedModels = async () => {
    try {
      const models = await scheduledWorkflowsService.getSupportedModels();
      setSupportedModels(models);
    } catch (err) {
      console.error('Error loading supported models:', err);
    }
  };

  const loadAiKeyInfo = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/teams/${teamId}/ai-keys`, {
        headers: { ...(await getAuthHeaders()) }
      });
      if (response.ok) {
        const data = await response.json();
        setAiKeys(data.keys ?? []);
      }
    } catch (err) {
      console.error('Error loading AI keys:', err);
    }
  };

  const handleSaveModels = async () => {
    setSavingModels(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/teams/${teamId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(await getAuthHeaders())
        },
        body: JSON.stringify({
          defaultPrimaryModel,
          defaultSecondaryModel,
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update AI model defaults');
      }

      const data = await response.json();
      setTeam(data.team);
      setSuccess('AI model defaults updated successfully');
    } catch (err) {
      console.error('Error updating AI model defaults:', err);
      setError(err instanceof Error ? err.message : 'Failed to update AI model defaults');
    } finally {
      setSavingModels(false);
    }
  };

  const handleChangeRole = async (userId: string, newRole: 'admin' | 'member') => {
    setActionLoading(userId);
    setError(null);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/teams/${teamId}/members/${userId}/role`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(await getAuthHeaders())
        },
        body: JSON.stringify({ role: newRole })
      });

      if (!response.ok) {
        throw new Error('Failed to update member role');
      }

      setSuccess(`Role updated to ${newRole}`);
      await loadTeamMembers();
    } catch (err) {
      console.error('Error updating role:', err);
      setError(err instanceof Error ? err.message : 'Failed to update role');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemoveMember = async (userId: string) => {
    if (!confirm('Are you sure you want to remove this member?')) {
      return;
    }

    setActionLoading(userId);
    setError(null);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/teams/${teamId}/members/${userId}`, {
        method: 'DELETE',
        headers: {
          ...(await getAuthHeaders())
        }
      });

      if (!response.ok) {
        throw new Error('Failed to remove member');
      }

      setSuccess('Member removed successfully');
      await loadTeamMembers();
    } catch (err) {
      console.error('Error removing member:', err);
      setError(err instanceof Error ? err.message : 'Failed to remove member');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancelInvitation = async (invitationId: string) => {
    if (!confirm('Are you sure you want to cancel this invitation?')) {
      return;
    }

    setActionLoading(invitationId);
    setError(null);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/teams/${teamId}/invitations/${invitationId}`, {
        method: 'DELETE',
        headers: {
          ...(await getAuthHeaders())
        }
      });

      if (!response.ok) {
        throw new Error('Failed to cancel invitation');
      }

      setSuccess('Invitation canceled successfully');
      await loadTeamInvitations();
    } catch (err) {
      console.error('Error canceling invitation:', err);
      setError(err instanceof Error ? err.message : 'Failed to cancel invitation');
    } finally {
      setActionLoading(null);
    }
  };

  const handleInviteMember = async (e: Event) => {
    e.preventDefault();

    if (!inviteEmail.trim()) {
      setError('Email is required');
      return;
    }

    if (!inviteEmail.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    setInviting(true);
    setError(null);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/teams/${teamId}/members`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await getAuthHeaders())
        },
        body: JSON.stringify({
          email: inviteEmail.trim(),
          role: inviteRole
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send invitation');
      }

      setSuccess(`Invitation sent to ${inviteEmail}`);
      setInviteEmail('');
      setInviteRole('member');
      setShowInviteModal(false);

      // Reload invitations list
      await loadTeamInvitations();
    } catch (err) {
      console.error('Error sending invitation:', err);
      setError(err instanceof Error ? err.message : 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  const getRoleIcon = (role: string) => {
    return role === 'admin' ? '' : '';
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <span class="badge badge-success badge-sm">Active</span>;
      case 'pending':
        return <span class="badge badge-warning badge-sm">Pending</span>;
      case 'suspended':
        return <span class="badge badge-error badge-sm">Suspended</span>;
      default:
        return <span class="badge badge-ghost badge-sm">{status}</span>;
    }
  };

  const handleUpgradePlan = async (interval: 'monthly' | 'annual') => {
    setUpgradingPlan(true);
    setError(null);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/teams/${teamId}/billing/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await getAuthHeaders())
        },
        body: JSON.stringify({ interval })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create checkout session');
      }

      // Redirect to Stripe checkout
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      console.error('Error starting checkout:', err);
      setError(err instanceof Error ? err.message : 'Failed to start checkout');
      setUpgradingPlan(false);
    }
  };

  const resetAiKeyForm = () => {
    setAiKeyInput('');
    setAiLabelInput('');
    setShowAddAiKey(false);
  };

  const handleSaveAiKey = async () => {
    if (!aiKeyInput.trim()) return;
    setSavingAiKey(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/teams/${teamId}/ai-keys`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await getAuthHeaders())
        },
        body: JSON.stringify({
          provider: aiProviderInput,
          apiKey: aiKeyInput.trim(),
          ...(aiLabelInput.trim() ? { label: aiLabelInput.trim() } : {}),
          // `models` is omitted: per-model scoping is not exposed in the UI yet.
        })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to save API key');

      setAiKeys((prev) => {
        const added: TeamAiKeyRef = data.key;
        // The server clears the default flag on sibling keys for the same provider.
        const siblings = added.isDefault
          ? prev.map((k) => (k.provider === added.provider ? { ...k, isDefault: false } : k))
          : prev;
        return [...siblings, added];
      });
      resetAiKeyForm();
      setSuccess('API key added');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save API key');
    } finally {
      setSavingAiKey(false);
    }
  };

  const handleDeleteAiKey = async (keyId: string) => {
    setDeletingAiKeyId(keyId);
    setError(null);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/teams/${teamId}/ai-keys/${keyId}`, {
        method: 'DELETE',
        headers: { ...(await getAuthHeaders()) }
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to remove API key');
      }

      // Refetch so a promoted default is reflected accurately.
      await loadAiKeyInfo();
      setSuccess('API key removed');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove API key');
    } finally {
      setDeletingAiKeyId(null);
    }
  };

  const handleMakeDefaultAiKey = async (keyId: string) => {
    setUpdatingAiKeyId(keyId);
    setError(null);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/teams/${teamId}/ai-keys/${keyId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(await getAuthHeaders())
        },
        body: JSON.stringify({ isDefault: true })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to update key');

      const provider = aiKeys.find((k) => k.keyId === keyId)?.provider;
      setAiKeys((prev) => prev.map((k) =>
        k.provider === provider ? { ...k, isDefault: k.keyId === keyId } : k
      ));
      setSuccess('Default key updated');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update key');
    } finally {
      setUpdatingAiKeyId(null);
    }
  };

  const handleManageSubscription = async () => {
    setManagingSubscription(true);
    setError(null);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/teams/${teamId}/billing/portal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await getAuthHeaders())
        },
        body: JSON.stringify({
          returnUrl: window.location.href
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to open subscription portal');
      }

      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      console.error('Error opening subscription portal:', err);
      setError(err instanceof Error ? err.message : 'Failed to open subscription portal');
    } finally {
      setManagingSubscription(false);
    }
  };

  const handleSave = async (e: Event) => {
    e.preventDefault();

    if (!teamName.trim()) {
      setError('Team name is required');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/teams/${teamId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(await getAuthHeaders())
        },
        body: JSON.stringify({
          name: teamName.trim(),
          description: description.trim() || undefined
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update team settings');
      }

      const data = await response.json();
      setTeam(data.team);
      setSuccess('Team settings updated successfully');
    } catch (err) {
      console.error('Error updating team settings:', err);
      setError(err instanceof Error ? err.message : 'Failed to update team settings');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTeam = async () => {
    if (!confirm(`Are you sure you want to delete "${team?.name}"? This action cannot be undone.`)) {
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/teams/${teamId}`, {
        method: 'DELETE',
        headers: {
          ...(await getAuthHeaders())
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete team');
      }

      // Redirect to teams page after successful deletion
      window.location.href = '/teams';
    } catch (err) {
      console.error('Error deleting team:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete team');
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div class="flex-1 overflow-auto">
        <div class="container mx-auto p-8 max-w-4xl">
          <div class="flex items-center justify-center h-64">
            <span class="loading loading-spinner loading-lg"></span>
          </div>
          </div>
      </div>
    </MainLayout>
  );
}

  if (error && !team) {
    return (
      <MainLayout>
        <div class="flex-1 overflow-auto">
          <div class="container mx-auto p-8 max-w-4xl">
            <div class="alert alert-error mb-4">
              <span>{error}</span>
            </div>
            <a href="/teams" class="btn btn-ghost">← Back to Teams</a>
          </div>
        </div>
      </MainLayout>
    );
  }

  // TODO immediately - userRole is any, but it should be tied to a legit enum or type
  if (!team || userRole !== 'admin') {
    return (
      <MainLayout>
        <div class="flex-1 overflow-auto">
          <div class="container mx-auto p-8 max-w-4xl">
            <div class="alert alert-error mb-4">
              <span>You don't have permission to manage this team's settings</span>
            </div>
            <a href={`/teams/${teamId}`} class="btn btn-ghost">← Back to Team</a>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div class="flex-1 overflow-auto bg-base-200/30">

      <div class="container mx-auto p-6 md:p-8 max-w-5xl">
        {/* Header */}
        <div class="mb-8">
          <a href={`/teams/${teamId}`} class="btn btn-ghost btn-sm gap-2 mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
            Back to Team
          </a>
          <div>
            <h1 class="text-4xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent mb-2">
              Team Settings
            </h1>
            <p class="text-base-content/60 text-lg">
              Manage <span class="font-semibold text-base-content">{team.name}</span>'s settings and preferences
            </p>
          </div>
        </div>

        {/* Status Messages */}
        {error && (
          <div class="alert alert-error shadow-lg mb-6">
            <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current flex-shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div class="alert alert-success shadow-lg mb-6">
            <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current flex-shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{success}</span>
          </div>
        )}

        {/* Settings Tabs */}
        <div class="bg-base-100 rounded-2xl shadow-xl p-2 mb-8">
          <div class="tabs tabs-boxed bg-transparent gap-2">
            <button
              class={`tab transition-all duration-200 ${activeTab === 'general' ? 'tab-active bg-primary text-primary-content font-semibold shadow-md' : 'hover:bg-base-200'}`}
              onClick={() => setActiveTab('general')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              General
            </button>
            <button
              class={`tab transition-all duration-200 ${activeTab === 'ai-models' ? 'tab-active bg-primary text-primary-content font-semibold shadow-md' : 'hover:bg-base-200'}`}
              onClick={() => setActiveTab('ai-models')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              AI Models
            </button>
            <button
              class={`tab transition-all duration-200 ${activeTab === 'ai-provider' ? 'tab-active bg-primary text-primary-content font-semibold shadow-md' : 'hover:bg-base-200'}`}
              onClick={() => setActiveTab('ai-provider')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
              AI Provider
            </button>
            <button
              class={`tab transition-all duration-200 ${activeTab === 'members' ? 'tab-active bg-primary text-primary-content font-semibold shadow-md' : 'hover:bg-base-200'}`}
              onClick={() => setActiveTab('members')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              Members
            </button>
            <button
              class={`tab transition-all duration-200 ${activeTab === 'billing' ? 'tab-active bg-primary text-primary-content font-semibold shadow-md' : 'hover:bg-base-200'}`}
              onClick={() => setActiveTab('billing')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
              Billing
            </button>
            <button
              class={`tab transition-all duration-200 ${activeTab === 'usage' ? 'tab-active bg-primary text-primary-content font-semibold shadow-md' : 'hover:bg-base-200'}`}
              onClick={() => setActiveTab('usage')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              Usage
            </button>
            <button
              class={`tab transition-all duration-200 ${activeTab === 'danger' ? 'tab-active bg-error text-error-content font-semibold shadow-md' : 'hover:bg-base-200'}`}
              onClick={() => setActiveTab('danger')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              Danger Zone
            </button>
          </div>
        </div>

        {/* General Settings */}
        {activeTab === 'general' && (
          <div class="card bg-base-100 shadow-xl hover:shadow-2xl transition-shadow duration-300 border border-base-300">
          <div class="card-body p-8">
            <div class="flex items-center gap-3 mb-6">
              <div class="bg-primary/10 p-3 rounded-lg">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h2 class="text-2xl font-bold">Basic Information</h2>
                <p class="text-sm text-base-content/60">Update your team's basic details</p>
              </div>
            </div>

            <form onSubmit={handleSave}>
              <div class="form-control w-full mb-6">
                <label class="label">
                  <span class="label-text font-semibold text-base">Team Name *</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g., Engineering Team, Marketing Team"
                  class="input input-bordered input-lg w-full focus:input-primary transition-colors"
                  value={teamName}
                  onChange={(e) => setTeamName((e.target as HTMLInputElement).value)}
                  maxLength={50}
                  required
                />
                <label class="label">
                  <span class="label-text-alt text-base-content/50">This name will be visible to all team members</span>
                </label>
              </div>

              <div class="form-control w-full mb-8">
                <label class="label">
                  <span class="label-text font-semibold text-base">Description (Optional)</span>
                </label>
                <div class="w-full">
                  <textarea
                    placeholder="Use cases: automate"
                    class="textarea textarea-bordered textarea-lg h-28 focus:textarea-primary transition-colors resize-none w-full"
                    value={description}
                    onChange={(e) => setDescription((e.target as HTMLTextAreaElement).value)}
                    maxLength={200}
                  />
                </div>
                <label class="label">
                  <span class="label-text-alt"></span>
                  <span class={`label-text-alt ${description.length > 180 ? 'text-warning' : 'text-base-content/50'}`}>
                    {description.length}/200 characters
                  </span>
                </label>
              </div>

              <div class="divider"></div>

              <div class="flex justify-end gap-3">
                <button
                  type="button"
                  class="btn btn-ghost"
                  onClick={() => {
                    setTeamName(team?.name || '');
                    setDescription(team?.description || '');
                  }}
                >
                  Reset
                </button>
                <button
                  type="submit"
                  class="btn btn-primary btn-lg gap-2 shadow-lg"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <span class="loading loading-spinner loading-sm"></span>
                      Saving Changes...
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                      </svg>
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
        )}

        {/* AI Models Tab */}
        {activeTab === 'ai-models' && (
          <div class="card bg-base-100 shadow-xl hover:shadow-2xl transition-shadow duration-300 border border-base-300">
            <div class="card-body p-8">
              <div class="flex items-center gap-3 mb-6">
                <div class="bg-primary/10 p-3 rounded-lg">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h2 class="text-2xl font-bold">Default AI Models</h2>
                  <p class="text-sm text-base-content/60">Set team-wide default models for workflows</p>
                </div>
              </div>

              <div class="bg-info/10 border border-info/20 rounded-xl p-4 mb-6">
                <p class="text-sm text-base-content/80">
                  These defaults apply to all workflows that don't specify their own model. Individual workflows can override these settings from their configuration.
                </p>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div class="form-control">
                  <label class="label">
                    <span class="label-text font-semibold text-base">Primary Model (Planner)</span>
                  </label>
                  <select
                    class="select select-bordered w-full"
                    value={defaultPrimaryModel}
                    onChange={(e) => setDefaultPrimaryModel((e.target as HTMLSelectElement).value)}
                  >
                    <option value="">System Default</option>
                    {supportedModels.map((model) => (
                      <option key={model.id} value={model.id}>
                        {model.label}
                      </option>
                    ))}
                  </select>
                  <label class="label">
                    <span class="label-text-alt text-base-content/50">Used for planning and reasoning steps</span>
                  </label>
                </div>

                <div class="form-control">
                  <label class="label">
                    <span class="label-text font-semibold text-base">Secondary Model (Executor)</span>
                  </label>
                  <select
                    class="select select-bordered w-full"
                    value={defaultSecondaryModel}
                    onChange={(e) => setDefaultSecondaryModel((e.target as HTMLSelectElement).value)}
                  >
                    <option value="">System Default</option>
                    {supportedModels.map((model) => (
                      <option key={model.id} value={model.id}>
                        {model.label}
                      </option>
                    ))}
                  </select>
                  <label class="label">
                    <span class="label-text-alt text-base-content/50">Used for executing tool calls and actions</span>
                  </label>
                </div>
              </div>

              <div class="divider"></div>

              <div class="flex justify-end">
                <button
                  class="btn btn-primary btn-lg gap-2 shadow-lg"
                  onClick={handleSaveModels}
                  disabled={savingModels}
                >
                  {savingModels ? (
                    <>
                      <span class="loading loading-spinner loading-sm"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                      </svg>
                      Save Defaults
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* AI Provider Tab */}
        {activeTab === 'ai-provider' && (
          <div class="card bg-base-100 shadow-xl hover:shadow-2xl transition-shadow duration-300 border border-base-300">
            <div class="card-body p-8">
              <div class="flex items-center gap-3 mb-6">
                <div class="bg-primary/10 p-3 rounded-lg">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                  </svg>
                </div>
                <div>
                  <h2 class="text-2xl font-bold">AI Keys (BYOK)</h2>
                  <p class="text-sm text-base-content/60">Bring your own provider keys. Zenmako uses them for every AI call — we never charge for tokens. Add as many keys as you like, including several for the same provider.</p>
                </div>
              </div>

              {aiKeys.length > 0 && (
                <div class="space-y-3 mb-6">
                  {aiKeys.map((key) => (
                    <div key={key.keyId} class="flex flex-wrap items-center gap-3 p-4 rounded-lg border border-base-300 bg-base-200/40">
                      <div class="flex-1 min-w-[16rem]">
                        <div class="flex items-center gap-2 flex-wrap">
                          <span class="font-semibold">{key.label}</span>
                          <span class="badge badge-sm badge-outline">{PROVIDER_LABELS[key.provider]}</span>
                          {key.isDefault && <span class="badge badge-sm badge-primary">Default</span>}
                        </div>
                        <div class="text-sm text-base-content/60 mt-1">
                          {key.lastFour ? <span class="font-mono">••••{key.lastFour}</span> : <span>key stored</span>}
                          {key.models && key.models.length > 0 ? (
                            <span> · used for {key.models.map((m) => m.split('/').pop()).join(', ')}</span>
                          ) : (
                            <span> · used for any {PROVIDER_LABELS[key.provider]} model</span>
                          )}
                        </div>
                      </div>

                      {userRole === 'admin' && (
                        <div class="flex items-center gap-2 ml-auto">
                          {!key.isDefault && (
                            <button
                              class="btn btn-xs btn-ghost"
                              onClick={() => handleMakeDefaultAiKey(key.keyId)}
                              disabled={updatingAiKeyId === key.keyId}
                            >
                              {updatingAiKeyId === key.keyId
                                ? <span class="loading loading-spinner loading-xs"></span>
                                : 'Make default'}
                            </button>
                          )}
                          <button
                            class="btn btn-xs btn-ghost text-error"
                            onClick={() => handleDeleteAiKey(key.keyId)}
                            disabled={deletingAiKeyId === key.keyId}
                          >
                            {deletingAiKeyId === key.keyId
                              ? <span class="loading loading-spinner loading-xs"></span>
                              : 'Remove'}
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {aiKeys.length === 0 && (
                <div class="alert alert-warning mb-6">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <span>
                    {userRole === 'admin'
                      ? 'No AI keys configured yet. Add one to start running agents on your own account.'
                      : 'No AI provider key configured. Ask your team admin to add one.'}
                  </span>
                </div>
              )}

              {userRole === 'admin' && !showAddAiKey && (
                <button class="btn btn-primary btn-sm" onClick={() => setShowAddAiKey(true)}>
                  Add a key
                </button>
              )}

              {userRole === 'admin' && showAddAiKey && (
                <div class="space-y-4 p-4 rounded-lg border border-base-300">
                  <div class="form-control">
                    <label class="label">
                      <span class="label-text font-semibold">Provider</span>
                    </label>
                    <select
                      class="select select-bordered w-full max-w-xs"
                      value={aiProviderInput}
                      onChange={(e) => setAiProviderInput((e.target as HTMLSelectElement).value as AiProvider)}
                    >
                      <option value="openai">OpenAI</option>
                      <option value="google">Google AI (Gemini)</option>
                    </select>
                  </div>

                  <div class="form-control">
                    <label class="label">
                      <span class="label-text font-semibold">Name</span>
                      <span class="label-text-alt text-base-content/50">Optional</span>
                    </label>
                    <input
                      type="text"
                      class="input input-bordered w-full max-w-lg"
                      placeholder={aiProviderInput === 'openai' ? 'e.g. OpenAI — gpt-4o billing' : 'e.g. Gemini — research'}
                      value={aiLabelInput}
                      onInput={(e) => setAiLabelInput((e.target as HTMLInputElement).value)}
                    />
                  </div>

                  <div class="form-control">
                    <label class="label">
                      <span class="label-text font-semibold">API Key</span>
                      <span class="label-text-alt text-base-content/50">Never stored in plaintext</span>
                    </label>
                    <input
                      type="password"
                      class="input input-bordered w-full max-w-lg font-mono"
                      placeholder={aiProviderInput === 'openai' ? 'sk-...' : 'AIza...'}
                      value={aiKeyInput}
                      onInput={(e) => setAiKeyInput((e.target as HTMLInputElement).value)}
                    />
                    <label class="label">
                      <span class="label-text-alt text-base-content/50">
                        {aiProviderInput === 'openai'
                          ? 'Find your key at platform.openai.com/api-keys'
                          : 'Find your key at aistudio.google.com/app/apikey'}
                      </span>
                    </label>
                  </div>

                  {/*
                    Per-model key scoping is supported by the API but not exposed here yet.
                    Keys added from this form apply to any model for their provider.
                  */}

                  <div class="flex items-center gap-2">
                    <button
                      class="btn btn-primary"
                      onClick={handleSaveAiKey}
                      disabled={savingAiKey || !aiKeyInput.trim()}
                    >
                      {savingAiKey ? (
                        <>
                          <span class="loading loading-spinner loading-sm"></span>
                          Validating & saving...
                        </>
                      ) : 'Add key'}
                    </button>
                    <button class="btn btn-ghost" onClick={resetAiKeyForm} disabled={savingAiKey}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}

            </div>
          </div>
        )}

        {/* Members Tab */}
        {activeTab === 'members' && (
          <div class="card bg-base-100 shadow-xl hover:shadow-2xl transition-shadow duration-300 border border-base-300">
            <div class="card-body p-8">
              <div class="flex justify-between items-center mb-6">
                <div class="flex items-center gap-3">
                  <div class="bg-primary/10 p-3 rounded-lg">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  </div>
                  <div>
                    <h2 class="text-2xl font-bold">Team Members</h2>
                    <p class="text-sm text-base-content/60">{members.length} {members.length === 1 ? 'member' : 'members'} in your team</p>
                  </div>
                </div>
                <button onClick={() => setShowInviteModal(true)} class="btn btn-primary gap-2 shadow-lg">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                  </svg>
                  Invite Member
                </button>
              </div>

              {members.length === 0 ? (
                <div class="text-center py-12 bg-base-200/50 rounded-xl">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-16 w-16 mx-auto text-base-content/30 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <p class="text-base-content/70 text-lg font-medium">No members found</p>
                  <p class="text-base-content/50 text-sm mt-2">Invite team members to get started</p>
                </div>
              ) : (
                <div class="overflow-x-auto rounded-xl border border-base-300">
                  <table class="table table-zebra">
                    <thead class="bg-base-200">
                      <tr>
                        <th class="font-bold text-base">Member</th>
                        <th class="font-bold text-base">Role</th>
                        <th class="font-bold text-base">Status</th>
                        <th class="font-bold text-base">Joined</th>
                        <th class="font-bold text-base">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {members.map((member) => (
                        <tr key={member.userId}>
                          <td>
                            <div class="flex items-center gap-3">
                              <div class="avatar placeholder">
                                <div class="bg-neutral text-neutral-content rounded-full w-8 h-8">
                                  <span class="text-xs">{member.name?.charAt(0)?.toUpperCase() || '?'}</span>
                                </div>
                              </div>
                              <div>
                                <div class="font-semibold">{member.name}</div>
                                <div class="text-sm text-base-content/70">{member.email}</div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div class="flex items-center gap-2">
                              {getRoleIcon(member.role)}
                              <span class="capitalize">{member.role}</span>
                            </div>
                          </td>
                          <td>{getStatusBadge(member.status)}</td>
                          <td>{new Date(member.joinedAt).toLocaleDateString()}</td>
                          <td>
                            {userRole === 'admin' && member.userId !== members.find(m => m.role === 'admin' && m.userId === member.userId)?.userId && (
                              <div class="dropdown dropdown-end">
                                <button
                                  class="btn btn-ghost btn-xs"
                                  disabled={actionLoading === member.userId}
                                >
                                  {actionLoading === member.userId ? '...' : '⋯'}
                                </button>
                                <ul class="dropdown-content menu p-2 shadow bg-base-100 rounded-box w-52">
                                  <li>
                                    <button
                                      onClick={() => handleChangeRole(member.userId, member.role === 'admin' ? 'member' : 'admin')}
                                    >
                                      Make {member.role === 'admin' ? 'Member' : 'Admin'}
                                    </button>
                                  </li>
                                  <li>
                                    <button
                                      onClick={() => handleRemoveMember(member.userId)}
                                      class="text-error"
                                    >
                                      Remove Member
                                    </button>
                                  </li>
                                </ul>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Pending Invitations Section */}
            <div class="divider my-8"></div>
            <div class="mb-4 p-8">
              <div class="flex items-center gap-3 mb-6">
                <div class="bg-secondary/10 p-2 rounded-lg">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h3 class="text-xl font-bold">Pending Invitations</h3>
                  <p class="text-sm text-base-content/60">{invitations.length} {invitations.length === 1 ? 'invitation' : 'invitations'} waiting for response</p>
                </div>
              </div>

              {invitations.length === 0 ? (
                <div class="text-center py-8 bg-base-200/50 rounded-xl">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 mx-auto text-base-content/30 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                  </svg>
                  <p class="text-base-content/60 font-medium">No pending invitations</p>
                </div>
              ) : (
                <div class="overflow-x-auto rounded-xl border border-base-300">
                  <table class="table table-zebra">
                    <thead class="bg-base-200">
                      <tr>
                        <th class="font-bold text-base">Email</th>
                        <th class="font-bold text-base">Role</th>
                        <th class="font-bold text-base">Status</th>
                        <th class="font-bold text-base">Sent</th>
                        <th class="font-bold text-base">Expires</th>
                        <th class="font-bold text-base">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invitations.map((invitation) => (
                        <tr key={invitation.invitationId}>
                          <td>
                            <div class="flex items-center gap-3">
                              <div class="avatar placeholder">
                                <div class="bg-neutral text-neutral-content rounded-full w-8 h-8">
                                  <span class="text-xs">📧</span>
                                </div>
                              </div>
                              <div>
                                <div class="font-semibold">{invitation.email}</div>
                                <div class="text-sm text-base-content/70">Invitation pending</div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div class="flex items-center gap-2">
                              {getRoleIcon(invitation.role)}
                              <span class="capitalize">{invitation.role}</span>
                            </div>
                          </td>
                          <td>{getStatusBadge(invitation.status)}</td>
                          <td>{new Date(invitation.createdAt).toLocaleDateString()}</td>
                          <td>{new Date(invitation.expiresAt).toLocaleDateString()}</td>
                          <td>
                            {userRole === 'admin' && (
                              <button
                                class="btn btn-ghost btn-xs text-error"
                                onClick={() => handleCancelInvitation(invitation.invitationId)}
                                disabled={actionLoading === invitation.invitationId}
                              >
                                {actionLoading === invitation.invitationId ? '...' : 'Cancel'}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Billing Tab */}
        {activeTab === 'billing' && (
          <div class="space-y-6">
            {/* Current Plan */}
            <div class="card bg-base-100 shadow-xl hover:shadow-2xl transition-shadow duration-300 border border-base-300">
              <div class="card-body p-8">
                <div class="flex items-center gap-3 mb-6">
                  <div class="bg-success/10 p-3 rounded-lg">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h2 class="text-2xl font-bold">Current Plan</h2>
                    <p class="text-sm text-base-content/60">Your subscription details and limits</p>
                  </div>
                </div>
                {billingInfo ? (
                  <>
                  <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div class="stat bg-gradient-to-br from-primary/10 to-primary/5 rounded-xl p-6 shadow-md border border-primary/20">
                      <div class="stat-figure text-primary">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                        </svg>
                      </div>
                      <div class="stat-title font-semibold text-base-content/70">Plan</div>
                      <div class="stat-value text-primary text-3xl capitalize">{billingInfo.plan === 'free' ? 'Free' : 'Zenmako'}</div>
                      {billingInfo.plan !== 'free' && billingInfo.interval && (
                        <div class="stat-desc capitalize">{billingInfo.interval === 'annual' ? '$60/yr · Annual' : '$6/mo · Monthly'}</div>
                      )}
                    </div>
                    <div class="stat bg-gradient-to-br from-success/10 to-success/5 rounded-xl p-6 shadow-md border border-success/20">
                      <div class="stat-figure text-success">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div class="stat-title font-semibold text-base-content/70">Status</div>
                      <div class="stat-value">
                        <span class={`badge ${billingInfo.status === 'active' ? 'badge-success' : billingInfo.status === 'cancelled' ? 'badge-error' : 'badge-warning'} badge-lg capitalize`}>
                          {billingInfo.status}
                        </span>
                      </div>
                    </div>
                    <div class="stat bg-gradient-to-br from-secondary/10 to-secondary/5 rounded-xl p-6 shadow-md border border-secondary/20">
                      <div class="stat-figure text-secondary">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                      </div>
                      <div class="stat-title font-semibold text-base-content/70">Members</div>
                      <div class="stat-value text-secondary text-3xl">{members.length}/{billingInfo.maxMembers || '∞'}</div>
                      {billingInfo.maxMembers > 0 && members.length >= Math.ceil(billingInfo.maxMembers * 0.8) && members.length < billingInfo.maxMembers && (
                        <div class="stat-desc text-warning mt-1">Approaching limit - contact us to raise quota</div>
                      )}
                      {billingInfo.maxMembers > 0 && members.length >= billingInfo.maxMembers && (
                        <div class="stat-desc text-error mt-1">Limit reached - contact us to raise quota</div>
                      )}
                    </div>
                  </div>
                  {billingInfo.plan !== 'free' && billingInfo.status === 'active' && (
                    <div class="mt-6 pt-6 border-t border-base-300 flex items-center gap-4">
                      <button
                        class="btn btn-outline btn-sm"
                        onClick={handleManageSubscription}
                        disabled={managingSubscription}
                      >
                        {managingSubscription ? (
                          <>
                            <span class="loading loading-spinner loading-sm"></span>
                            Opening...
                          </>
                        ) : (
                          'Manage Subscription'
                        )}
                      </button>
                      <span class="text-xs text-base-content/50">
                        Update payment method, view invoices, or cancel
                      </span>
                    </div>
                  )}
                  </>
                ) : (
                  <div class="text-center py-8">
                    <div class="alert alert-warning shadow-lg">
                      <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current flex-shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <div class="text-left">
                        <div class="font-semibold">Billing information not available</div>
                        <div class="text-sm">API endpoint not implemented. Correct fix: automatically add team to free plan when it is set up.</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Plan & Pricing */}
            <div class="card bg-base-100 shadow-xl hover:shadow-2xl transition-shadow duration-300 border border-base-300">
              <div class="card-body p-8">
                <div class="flex items-center gap-3 mb-6">
                  <div class="bg-warning/10 p-3 rounded-lg">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-warning" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div>
                    <h2 class="text-2xl font-bold">Plan & Pricing</h2>
                    <p class="text-sm text-base-content/60">One simple plan. Two billing options.</p>
                  </div>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl">
                  {/* Monthly Plan */}
                  <div class={`card border-2 ${billingInfo?.plan !== 'free' && billingInfo?.interval === 'monthly' ? 'border-primary bg-gradient-to-br from-primary/5 to-primary/10' : 'border-primary/30 bg-base-200/30'} hover:shadow-lg transition-all`}>
                    <div class="card-body">
                      {billingInfo?.plan !== 'free' && billingInfo?.interval === 'monthly' && <div class="badge badge-primary badge-sm absolute top-4 right-4">Current</div>}
                      <h3 class="card-title text-xl">Monthly</h3>
                      <div class="my-4">
                        <span class="text-4xl font-bold">$6</span>
                        <span class="text-base-content/60"> / month</span>
                      </div>
                      <ul class="space-y-2 text-sm mb-4">
                        <li class="flex items-center gap-2"><svg class="h-4 w-4 text-success flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" /></svg>Unlimited workflows & members</li>
                        <li class="flex items-center gap-2"><svg class="h-4 w-4 text-success flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" /></svg>150+ integrations</li>
                        <li class="flex items-center gap-2"><svg class="h-4 w-4 text-success flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" /></svg>Cancel anytime</li>
                      </ul>
                      {billingInfo?.plan !== 'free' ? (
                        <button class="btn btn-outline" disabled>{billingInfo?.interval === 'monthly' ? 'Current Plan' : 'Not selected'}</button>
                      ) : (
                        <button
                          class="btn btn-outline btn-primary"
                          onClick={() => handleUpgradePlan('monthly')}
                          disabled={upgradingPlan}
                        >
                          {upgradingPlan ? <><span class="loading loading-spinner loading-sm"></span>Redirecting...</> : 'Subscribe — $6/mo'}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Annual Plan */}
                  <div class={`card border-2 ${billingInfo?.plan !== 'free' && billingInfo?.interval === 'annual' ? 'border-primary bg-gradient-to-br from-primary/5 to-primary/10' : 'border-primary bg-gradient-to-br from-primary/5 to-secondary/5'} hover:shadow-lg transition-all`}>
                    <div class="card-body">
                      {billingInfo?.plan !== 'free' && billingInfo?.interval === 'annual' && <div class="badge badge-primary badge-sm absolute top-4 right-4">Current</div>}
                      <div class="flex gap-2 mb-2">
                        <div class="badge badge-success badge-sm w-fit">Save 20%</div>
                      </div>
                      <h3 class="card-title text-xl">Annual</h3>
                      <div class="my-4">
                        <span class="text-4xl font-bold">$5</span>
                        <span class="text-base-content/60"> / month</span>
                        <div class="text-xs text-base-content/50 mt-1">$60 billed once a year</div>
                      </div>
                      <ul class="space-y-2 text-sm mb-4">
                        <li class="flex items-center gap-2"><svg class="h-4 w-4 text-success flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" /></svg>Unlimited workflows & members</li>
                        <li class="flex items-center gap-2"><svg class="h-4 w-4 text-success flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" /></svg>150+ integrations</li>
                        <li class="flex items-center gap-2"><svg class="h-4 w-4 text-success flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" /></svg>20% cheaper than monthly</li>
                      </ul>
                      {billingInfo?.plan !== 'free' ? (
                        <button class="btn btn-primary" disabled>{billingInfo?.interval === 'annual' ? 'Current Plan' : 'Not selected'}</button>
                      ) : (
                        <button
                          class="btn btn-primary"
                          onClick={() => handleUpgradePlan('annual')}
                          disabled={upgradingPlan}
                        >
                          {upgradingPlan ? <><span class="loading loading-spinner loading-sm"></span>Redirecting...</> : 'Subscribe — $60/yr'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Usage Tab */}
        {activeTab === 'usage' && (
          <div class="space-y-6">
            {/* Usage & Limits */}
            <div class="card bg-base-100 shadow-xl hover:shadow-2xl transition-shadow duration-300 border border-base-300">
              <div class="card-body p-8">
                <div class="flex items-center gap-3 mb-6">
                  <div class="bg-info/10 p-3 rounded-lg">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-info" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <div>
                    <h2 class="text-2xl font-bold">Usage & Limits</h2>
                    <p class="text-sm text-base-content/60">Track your team's resource consumption</p>
                  </div>
                </div>
                <div class="space-y-4">
                  <div>
                    <div class="flex justify-between items-center mb-2">
                      <span class="text-sm font-medium">Team Members</span>
                      <span class="text-sm text-base-content/70">
                        {billingInfo ? `${members.length}/${billingInfo.maxMembers || 'unlimited'}` : 'unknown'}
                      </span>
                    </div>
                    {billingInfo ? (
                      <progress
                        class="progress progress-primary w-full"
                        value={members.length}
                        max={billingInfo.maxMembers || 100}
                      ></progress>
                    ) : (
                      <div class="w-full h-2 bg-base-300 rounded"></div>
                    )}
                  </div>

                  <div>
                    <div class="flex justify-between items-center mb-2">
                      <span class="text-sm font-medium">Tokens This Month</span>
                      <span class="text-sm text-base-content/70">
                        {billingInfo ? `${billingInfo.tokensUsed || 0}/${billingInfo.maxTokens || 'unlimited'}` : 'unknown'}
                      </span>
                    </div>
                    {billingInfo ? (
                      <progress
                        class="progress progress-secondary w-full"
                        value={billingInfo.tokensUsed || 0}
                        max={billingInfo.maxTokens || 100}
                      ></progress>
                    ) : (
                      <div class="w-full h-2 bg-base-300 rounded"></div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Usage History */}
            <div class="card bg-base-100 shadow-xl hover:shadow-2xl transition-shadow duration-300 border border-base-300">
              <div class="card-body p-8">
                <div class="flex items-center gap-3 mb-6">
                  <div class="bg-secondary/10 p-3 rounded-lg">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <h2 class="text-2xl font-bold">Usage History</h2>
                    <p class="text-sm text-base-content/60">Historical usage data and trends</p>
                  </div>
                </div>
                <div class="text-center py-12 bg-base-200/50 rounded-xl">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-16 w-16 mx-auto text-base-content/30 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                  </svg>
                  <p class="text-base-content/70 text-lg font-medium">Usage analytics coming soon</p>
                  <p class="text-base-content/50 text-sm mt-2">Track your team's historical usage patterns</p>
                </div>
              </div>
            </div>

            {/* Team Activity */}
            <div class="card bg-base-100 shadow-xl hover:shadow-2xl transition-shadow duration-300 border border-base-300">
              <div class="card-body p-8">
                <div class="flex items-center gap-3 mb-6">
                  <div class="bg-accent/10 p-3 rounded-lg">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h2 class="text-2xl font-bold">Team Activity</h2>
                    <p class="text-sm text-base-content/60">Monitor team actions and events</p>
                  </div>
                </div>
                <div class="text-center py-12 bg-base-200/50 rounded-xl">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-16 w-16 mx-auto text-base-content/30 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                  </svg>
                  <p class="text-base-content/70 text-lg font-medium">Activity logs coming soon</p>
                  <p class="text-base-content/50 text-sm mt-2">View detailed team usage analytics</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Danger Zone Tab */}
        {activeTab === 'danger' && (
        <div class="card bg-base-100 shadow-xl border-2 border-error/30 hover:border-error/50 transition-all duration-300">
          <div class="card-body p-8">
            <div class="flex items-center gap-3 mb-6">
              <div class="bg-error/10 p-3 rounded-lg">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-error" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <h2 class="text-2xl font-bold text-error">Danger Zone</h2>
                <p class="text-sm text-base-content/60">Irreversible and destructive actions</p>
              </div>
            </div>

            <div class="alert alert-warning shadow-lg mb-6">
              <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current flex-shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div class="text-left">
                <div class="font-semibold">Warning: This action cannot be undone</div>
                <div class="text-sm">Deleting a team is permanent. All team data, members, and settings will be lost forever.</div>
              </div>
            </div>

            <div class="bg-error/5 border-2 border-error/20 rounded-xl p-6 hover:border-error/40 transition-colors">
              <div class="flex justify-between items-start gap-4">
                <div class="flex-1">
                  <div class="flex items-center gap-2 mb-2">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-error" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    <h3 class="text-lg font-bold">Delete Team</h3>
                  </div>
                  <p class="text-sm text-base-content/70">
                    Permanently delete "<span class="font-semibold text-base-content">{team.name}</span>" and remove all members. This action cannot be reversed.
                  </p>
                </div>
                <button
                  class="btn btn-error gap-2 shadow-lg"
                  onClick={handleDeleteTeam}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <span class="loading loading-spinner loading-sm"></span>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Delete Team
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
        )}
        </div>
      </div>

      {/* Invite Member Modal */}
      {showInviteModal && (
        <dialog class="modal modal-open">
          <div class="modal-box max-w-md">
            <h3 class="font-bold text-2xl mb-4">Invite Team Member</h3>

            <form onSubmit={handleInviteMember}>
              <div class="form-control w-full mb-4">
                <label class="label">
                  <span class="label-text font-semibold">Email Address *</span>
                </label>
                <input
                  type="email"
                  placeholder="colleague@example.com"
                  class="input input-bordered w-full"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail((e.target as HTMLInputElement).value)}
                  required
                  disabled={inviting}
                />
              </div>

              <div class="form-control w-full mb-6">
                <label class="label">
                  <span class="label-text font-semibold">Role</span>
                </label>
                <select
                  class="select select-bordered w-full"
                  value={inviteRole}
                  onChange={(e) => setInviteRole((e.target as HTMLSelectElement).value as 'admin' | 'member')}
                  disabled={inviting}
                >
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              <div class="bg-base-200 p-4 rounded-lg mb-6">
                <div class="text-sm text-base-content/70">
                  <p class="mb-2"><strong>Member:</strong> Can participate in team activities and view team resources</p>
                  <p><strong>Admin:</strong> Can manage team settings, invite/remove members, and has all member permissions</p>
                </div>
              </div>

              <div class="modal-action">
                <button
                  type="button"
                  class="btn btn-ghost"
                  onClick={() => {
                    setShowInviteModal(false);
                    setInviteEmail('');
                    setInviteRole('member');
                    setError(null);
                  }}
                  disabled={inviting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  class="btn btn-primary"
                  disabled={inviting}
                >
                  {inviting ? (
                    <>
                      <span class="loading loading-spinner loading-sm"></span>
                      Sending...
                    </>
                  ) : (
                    'Send Invitation'
                  )}
                </button>
              </div>
            </form>
          </div>
          <form method="dialog" class="modal-backdrop">
            <button onClick={() => {
              setShowInviteModal(false);
              setInviteEmail('');
              setInviteRole('member');
              setError(null);
            }}>close</button>
          </form>
        </dialog>
      )}
    </MainLayout>
  );
}
